import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import { Clock, Crown, LayoutGrid, Sprout, Tag, TrendingUp } from "lucide-react";
import { GENRES, STATUSES } from "@/lib/genre-status";
import { renderFilterUrl, toggleFilterUrl } from "@/lib/filter-utils";
import type { FilterState } from "@/lib/filter-utils";
import type { StoryGenre, StoryStatus } from "@/lib/actions/story-actions";
import { CROWN_DISCOVERY_FILTER_OPTIONS, type CrownDiscoveryFilter } from "@/lib/spotlights/discovery-filters";

// ─── Sub-components ──────────────────────────────────────

function SidebarFilterLink(t0) {
  const $ = _c(11);
  const {
    icon: Icon,
    label,
    href,
    active: t1,
    testId
  } = t0;
  const active = t1 === undefined ? false : t1;
  const t2 = active ? "page" : undefined;
  const t3 = `flex min-h-11 md:min-h-9 items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${active ? "bg-primary text-white font-medium" : "text-slate-600 dark:text-slate-400 hover:bg-primary/10 hover:text-primary"}`;
  let t4;
  if ($[0] !== Icon) {
    t4 = <Icon className="size-4" />;
    $[0] = Icon;
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  let t5;
  if ($[2] !== label || $[3] !== t4) {
    t5 = <span className="flex items-center gap-2">{t4} {label}</span>;
    $[2] = label;
    $[3] = t4;
    $[4] = t5;
  } else {
    t5 = $[4];
  }
  let t6;
  if ($[5] !== href || $[6] !== t2 || $[7] !== t3 || $[8] !== t5 || $[9] !== testId) {
    t6 = <Link href={href} data-testid={testId} aria-current={t2} className={t3}>{t5}</Link>;
    $[5] = href;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
    $[9] = testId;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  return t6;
}
function FilterPillLink(t0) {
  const $ = _c(5);
  const {
    label,
    href,
    active: t1,
    testId
  } = t0;
  const active = t1 === undefined ? false : t1;
  const t2 = `inline-flex min-h-11 md:min-h-0 items-center justify-center px-4 py-1.5 rounded-full border text-xs font-medium transition-all ${active ? "border-primary text-primary font-bold bg-primary/5" : "border-slate-200 dark:border-primary/20 text-slate-600 dark:text-slate-400 hover:border-primary/50"}`;
  let t3;
  if ($[0] !== href || $[1] !== label || $[2] !== t2 || $[3] !== testId) {
    t3 = <Link href={href} data-testid={testId} className={t2}>{label}</Link>;
    $[0] = href;
    $[1] = label;
    $[2] = t2;
    $[3] = testId;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  return t3;
}

/**
 * Small coral "Clear" chip shown to the right of a section heading when that
 * section has an active filter. Compact (so it never wraps the header) but
 * high-contrast against the purple sidebar so it reads as the escape hatch.
 */
function SectionClear(t0) {
  const $ = _c(3);
  const {
    href,
    testId
  } = t0;
  let t1;
  if ($[0] !== href || $[1] !== testId) {
    t1 = <Link href={href} data-testid={testId} className="inline-flex min-h-11 md:min-h-0 shrink-0 items-center rounded-full bg-accent/15 px-3 md:px-2 py-1 md:py-0.5 text-[11px] md:text-[10px] font-bold uppercase tracking-wide text-accent transition-colors hover:bg-accent/25">Clear</Link>;
    $[0] = href;
    $[1] = testId;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}

// ─── Main Component ──────────────────────────────────────

export interface TagData {
  id: string;
  name: string;
  label: string;
}
interface GenreSidebarProps {
  activeView?: "all";
  storyCount: number;
  /** Browse-only single-select Crown discovery filter. */
  activeDiscovery?: CrownDiscoveryFilter;
  showDiscoveryFilters?: boolean;
  /** All currently-selected genres (multi-select). */
  activeGenres?: StoryGenre[];
  activeStatus?: StoryStatus;
  /** All currently-selected tags (multi-select). */
  activeTags?: string[];
  /** Browse search query, preserved while sidebar filters change. */
  activeQuery?: string;
  /**
   * The current page's path genre, if any. Kept anchored in the path while
   * selected so toggling unrelated filters stays on this genre page.
   */
  anchorGenre?: StoryGenre;
  tags?: TagData[];
}

/**
 * The genre/status/tag filter sidebar for the browse + genre pages.
 * Genres and tags are multi-select; status is single-select. All filter
 * state lives in the URL for server-side filtering.
 */
export function GenreSidebar(t0) {
  const $ = _c(23);
  const {
    activeView,
    storyCount,
    activeDiscovery,
    showDiscoveryFilters: t1,
    activeGenres: t2,
    activeStatus,
    activeTags: t3,
    activeQuery,
    anchorGenre,
    tags: t4
  } = t0;
  const showDiscoveryFilters = t1 === undefined ? false : t1;
  let t5;
  if ($[0] !== activeDiscovery || $[1] !== activeQuery || $[2] !== activeStatus || $[3] !== activeView || $[4] !== anchorGenre || $[5] !== showDiscoveryFilters || $[6] !== storyCount || $[7] !== t2 || $[8] !== t3 || $[9] !== t4) {
    const activeGenres = t2 === undefined ? [] : t2;
    const activeTags = t3 === undefined ? [] : t3;
    const tags = t4 === undefined ? [] : t4;
    const filterState = {
      view: activeView,
      genres: activeGenres,
      discovery: activeDiscovery,
      status: activeStatus,
      tags: activeTags,
      q: activeQuery
    };
    const noGenresActive = activeGenres.length === 0;
    const allGenresHref = renderFilterUrl({
      view: activeView,
      genres: [],
      discovery: activeDiscovery,
      status: activeStatus,
      tags: activeTags,
      q: activeQuery
    }, undefined);
    let t6;
    if ($[11] !== anchorGenre) {
      t6 = g => g !== anchorGenre;
      $[11] = anchorGenre;
      $[12] = t6;
    } else {
      t6 = $[12];
    }
    const extraGenreCount = activeGenres.filter(t6).length;
    const clearGenresHref = renderFilterUrl({
      view: activeView,
      genres: anchorGenre ? [anchorGenre] : [],
      discovery: activeDiscovery,
      status: activeStatus,
      tags: activeTags,
      q: activeQuery
    }, anchorGenre);
    const clearDiscoveryHref = renderFilterUrl({
      view: activeView,
      genres: activeGenres,
      discovery: undefined,
      status: activeStatus,
      tags: activeTags,
      q: activeQuery
    });
    const clearStatusHref = renderFilterUrl({
      view: activeView,
      genres: activeGenres,
      discovery: activeDiscovery,
      status: undefined,
      tags: activeTags,
      q: activeQuery
    }, anchorGenre);
    const clearTagsHref = renderFilterUrl({
      view: activeView,
      genres: activeGenres,
      discovery: activeDiscovery,
      status: activeStatus,
      tags: [],
      q: activeQuery
    }, anchorGenre);
    let t7;
    if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <h3 className="text-slate-900 dark:text-white font-heading font-bold flex items-center gap-2"><LayoutGrid className="size-5 text-primary" /> Genres</h3>;
      $[13] = t7;
    } else {
      t7 = $[13];
    }
    const t8 = extraGenreCount > 0 && <SectionClear href={clearGenresHref} testId="clear-genres" />;
    let t9;
    if ($[14] !== t8) {
      t9 = <div className="mb-4 flex min-h-11 md:min-h-0 items-center justify-between gap-2">{t7}{t8}</div>;
      $[14] = t8;
      $[15] = t9;
    } else {
      t9 = $[15];
    }
    let t10;
    if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
      t10 = <span>All Genres</span>;
      $[16] = t10;
    } else {
      t10 = $[16];
    }
    const t11 = `text-[10px] px-2 py-0.5 rounded-full ${noGenresActive ? "bg-white/20" : "bg-slate-200 dark:bg-primary/20"}`;
    let t12;
    if ($[17] !== storyCount || $[18] !== t11) {
      t12 = <span className={t11}>{storyCount}</span>;
      $[17] = storyCount;
      $[18] = t11;
      $[19] = t12;
    } else {
      t12 = $[19];
    }
    let t13;
    if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
      t13 = <h3 className="text-slate-900 dark:text-white font-heading font-bold flex items-center gap-2"><Clock className="size-5 text-primary" /> Status</h3>;
      $[20] = t13;
    } else {
      t13 = $[20];
    }
    const t14 = activeStatus && <SectionClear href={clearStatusHref} testId="clear-status" />;
    let t15;
    if ($[21] !== t14) {
      t15 = <div className="mb-4 flex min-h-11 md:min-h-0 items-center justify-between gap-2">{t13}{t14}</div>;
      $[21] = t14;
      $[22] = t15;
    } else {
      t15 = $[22];
    }
    t5 = <aside data-testid="genre-sidebar" className="w-full md:w-64 flex-shrink-0 flex flex-col gap-8">{showDiscoveryFilters && <div><div className="mb-4 flex min-h-11 items-center justify-between gap-2 md:min-h-0"><h3 className="flex items-center gap-2 font-heading font-bold text-slate-900 dark:text-white"><Crown className="size-5 text-amber-500" /> Crown Discovery</h3>{activeDiscovery && <SectionClear href={clearDiscoveryHref} testId="clear-discovery" />}</div><div className="flex flex-col gap-1">{CROWN_DISCOVERY_FILTER_OPTIONS.map(option => {
            const Icon = option.value === "most-crowned" ? Crown : option.value === "ascending" ? TrendingUp : Sprout;
            return <SidebarFilterLink key={option.value} icon={Icon} label={option.shortLabel} testId={`discovery-link-${option.value}`} href={toggleFilterUrl(filterState, {
              discovery: option.value
            }, anchorGenre)} active={activeDiscovery === option.value} />;
          })}</div></div>}<div>{t9}<div className="flex flex-col gap-1"><Link href={allGenresHref} data-testid="genre-link-all" className={`flex min-h-11 md:min-h-9 items-center justify-between px-3 py-2 rounded-lg text-sm transition-all ${noGenresActive ? "bg-primary text-white font-medium" : "text-slate-600 dark:text-slate-400 hover:bg-primary/10 hover:text-primary"}`}>{t10}{t12}</Link>{GENRES.map(g_0 => <SidebarFilterLink key={g_0.value} icon={g_0.icon} label={g_0.label} testId={`genre-link-${g_0.value}`} href={toggleFilterUrl(filterState, {
            genre: g_0.value
          }, anchorGenre)} active={activeGenres.includes(g_0.value)} />)}</div></div><div>{t15}<div className="flex flex-wrap gap-2">{STATUSES.map(s => <FilterPillLink key={s.value} label={s.label} testId={`status-link-${s.value}`} href={toggleFilterUrl(filterState, {
            status: s.value
          }, anchorGenre)} active={activeStatus === s.value} />)}</div></div>{tags.length > 0 && <div><div className="mb-4 flex min-h-11 md:min-h-0 items-center justify-between gap-2"><h3 className="text-slate-900 dark:text-white font-heading font-bold flex items-center gap-2"><Tag className="size-5 text-primary" /> Popular Tags</h3>{activeTags.length > 0 && <SectionClear href={clearTagsHref} testId="clear-tags" />}</div><div className="flex flex-wrap gap-2">{tags.map(tag => <FilterPillLink key={tag.id} label={`#${tag.label}`} testId={`tag-link-${tag.name}`} href={toggleFilterUrl(filterState, {
            tag: tag.name
          }, anchorGenre)} active={activeTags.includes(tag.name)} />)}</div></div>}</aside>;
    $[0] = activeDiscovery;
    $[1] = activeQuery;
    $[2] = activeStatus;
    $[3] = activeView;
    $[4] = anchorGenre;
    $[5] = showDiscoveryFilters;
    $[6] = storyCount;
    $[7] = t2;
    $[8] = t3;
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  return t5;
}